/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  ReportService,
} from '@gh/builder-shared';
import { DialogService, FormMode, NotificationService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { ChecklistsBusinessService } from '../../resources';
import {
  ChecklistDetails,
  ChklClause,
  ChklLevel,
  ChklMasterInfo,
  ChklSection,
  ChklSectionInfo,
  chklSelectionToTreePath,
} from '../../resources/checklists.model';
import { TranslationService } from '@gh/core-mls';
import {
  ActionFlow,
  AppRouter,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
} from '@gh/common-shared';
import { ChecklistsDetailFormService } from './checklists-detail-form.service';
import { Observable } from 'rxjs';
import { filter$, first$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';
import { createDefaultTreeOptions, goToNodeByPath, treeOptionsEnableDragging } from '@gh/core-ui-ext/tree';
import { ITreeState, TREE_ACTIONS, TreeComponent } from 'angular-tree-component';
import { FocusedCell } from '@gh/core-ui-grid';
// tslint:disable-next-line:no-submodule-imports
import { ITreeNode } from 'angular-tree-component/dist/defs/api';
import { BuilderPermission, ChecklistElementType, SystemRoleBit } from '@gh/builder-shared-data';
// tslint:disable-next-line:no-submodule-imports
import { TreeOptions } from 'angular-tree-component/dist/models/tree-options.model';
import { isMessageBasket } from '@gh/core-messages';

@Component({
             selector: 'gh-checklists-detail',
             template: require('./checklists-detail.component.html'),
             changeDetection: ChangeDetectionStrategy.OnPush,
             providers: [
               ChecklistsDetailFormService,
             ],
           })
export class ChecklistsDetailComponent extends AbstractFormComponent<ChecklistDetails> implements OnInit {
  title: string;
  currentElementType: Maybe<string>;
  isParent: boolean;
  notEditable: boolean;
  isShowItems: boolean = true;
  handleError: boolean;
  isParentEditable: boolean;
  maxCountOfSignature: number = 5;
  treeOptions = createDefaultTreeOptions({
     idField: 'id',
     childrenField: 'checklistElements',
     allowDrag: true,
     allowDrop: (node, { parent }) => node.level === parent.level + 1,
     nodeClass: (node) => node.level === 1 ? 'node-expandable' : '',
     // We disable selection on click and handle this behavior yourself.
     // Selection on click is not suitable when click on buttons.
     actionMapping: {
       mouse: {
         click: TREE_ACTIONS.ACTIVATE,
       },
     },
   }) as TreeOptions;
  treeState: ITreeState = {};
  @ViewChild(TreeComponent, { static: true }) tree: TreeComponent;
  clauseFocusedCell$: Observable<Maybe<FocusedCell>>;

  constructor(private route: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              private context: BuilderUserContext,
              private translations: TranslationService,
              private businessService: ChecklistsBusinessService,
              public form: ChecklistsDetailFormService,
              private ngZone: NgZone,
              private dialogService: DialogService,
              private reportService: ReportService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private notifications: NotificationService,
              private navigationService: BuilderNavigationService,
              private router: AppRouter) {
    super();
  }
  get modeTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_checklist',
      [FormMode.EditMode]: 'lbl_edit_checklist',
      [FormMode.ViewMode]: 'lbl_view_checklist',
    }[this.form.mode];
  }

  get masterInfo(): FormStateSnapshot<ChklMasterInfo> {
    return this.form.value.masterInfo;
  }

  get checklistElements(): ChklSection[] {
    return this.form.value.checklistElements;
  }

  get isSectionSelected(): boolean {
    return this.form.selection.level === ChklLevel.Section;
  }

  get currentSection(): Maybe<ChklSection> {
    return this.form.selection.section;
  }

  get hasEditOrViewPermission(): boolean {
    return this.context.hasSystemRole(SystemRoleBit.Corporate)
    || this.context.hasSystemRole(SystemRoleBit.Master)
    || this.context.hasPermission(BuilderPermission.Setup.Checklists.EDIT)
    || this.context.hasPermission(BuilderPermission.Setup.Checklists.VIEW);
  }

  get completeFlow(): ActionFlow<FormFlowState<ChecklistDetails>> {
    return this.pipelines.complete({
       validateForm: this.commonActions.validateWithEffect(
         (state: FormFlowState<ChecklistDetails>) => !this.isAllValid() || !this.isCountOfSignaturesValid(state.value),
         (state: FormFlowState<ChecklistDetails>) => this.handleErrors(state.value),
       ),
       execute: this.commonActions.execute((state: FormFlowState<ChecklistDetails>) => this.businessService.save(state.value)),
       finish: (state: FormFlowState<ChecklistDetails>) => {
         const { value } = state;
         if (this.form.createMode) {
           if (!this.hasEditOrViewPermission) {
             this.router.navigateByUrl('/setup/checklists');
             return;
           }
           this.router.switchTo(['/setup/checklists', value.id]);
         } else {
           this.form.merge(value);
           this.form.value.version = value.version;
         }
         this.form.resetDirty();
         this.changeDetectorRef.detectChanges();
       },
        error: ({ error }) => {
         if (isMessageBasket(error)) {
           this.notifications.processAll(error);
         }
       },
     });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);
    this.isParent = details.isParent;
    this.isParentEditable = details.isParentEditable;

    this.notEditable = details.masterInfo.value.notEditable;

    this.form.selectionChanges.pipe(
      map$(chklSelectionToTreePath),
      filter$(Boolean),
      // wait while all processing of new/removed nodes is completed by tree component
      switchMap$((path) => this.ngZone.onMicrotaskEmpty.pipe(first$(), mapTo$(path))))
      .subscribe((path: any) => {
        this.treeState = goToNodeByPath(this.treeState, path);
        this.changeDetectorRef.markForCheck();
      });

    this.clauseFocusedCell$ = this.form.selectionChanges.pipe(map$((selection) => selection.clauseFocusedCell));

    this.form.build(details);

    this.treeOptions = treeOptionsEnableDragging(this.treeOptions, this.form.editable) as TreeOptions;

    this.updateTitle();

    this.form.changes.subscribe(() => this.changeDetectorRef.markForCheck());

  }

  isCountOfSignaturesValid(info: ChecklistDetails): boolean {
    const countOfSignatures = info.checklistElements.filter(
      (item) => item?.info?.value?.elementType === ChecklistElementType.Signature)
      ?.length;
    return countOfSignatures <= this.maxCountOfSignature;
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<ChklMasterInfo>): void {
    this.form.updateMasterInfo(masterInfo);
    this.notEditable = masterInfo.value.notEditable;
    this.changeDetectorRef.markForCheck();
  }

  onCurrentSectionInfoChange(sectionInfo: FormStateSnapshot<ChklSectionInfo>): void {
    const { currentSection } = this;
    if (currentSection && this.isSimilar(currentSection.info.value, sectionInfo.value)) {
      return;
    }
    this.currentElementType = sectionInfo.value.elementType;
    if (currentSection) {
      this.form.updateSectionInfo(currentSection, sectionInfo);
    }
  }

  onElementTypeChange(type: ChecklistElementType): void {
    this.isShowItems = false;
    setTimeout(() => {
      this.currentElementType = type;
      this.isShowItems = true;
      // tslint:disable-next-line:no-magic-numbers
    },         100);
  }

  onCurrentSectionClausesChange(clauses: ChklClause[]): void {
    const { currentSection } = this;

    if (currentSection) {
      this.form.updateSectionClauses(currentSection, clauses);
    }
  }

  onMoveNode(): void {
    this.form.moveNodes();
  }

  onSelectNode(node: ITreeNode): void {
    switch (node.level) {
      case ChklLevel.Section:
        this.form.selectSection(node.data);
        break;
      default:
        break;
    }
  }

  onSectionAdd(): void {
    const newSection = this.businessService.createNewSection();
    this.form.addSection(newSection);

    this.updateTree();

    this.form.selectSection(newSection);
  }
  onSectionRemove(section: ChklSection): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_checklist_element_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeSection(section);
        this.updateTree();
      });
  }
  onResetFocusedCell(): void {
    this.form.resetFocusedCell();
  }
  goToAel(): void {
    this.navigationService.goToAel({
     entityId: this.form.id,
     entityType: 'CHECKLIST',
     label: this.modeTitle,
   });
  }
  getSectionTitle(section: ChklSection): string {
    return getFormStateSnapshotValue(section.info).title;
  }

  isAllValid(): boolean {
    this.handleError = false;
    if (this.form.invalid) {
      this.handleError = true;
      return false;
    }
    const elements = this.checklistElements;
    return !elements.find((item) => !item.info.value.title || !item.info.value.elementType);
  }

  handleErrors(info: ChecklistDetails): void {
    const elements = this.checklistElements;
    const errorElement = elements.find((item) => !item.info.value.title || !item.info.value.elementType);
    if (!this.isCountOfSignaturesValid(info)) {
      this.notifications.error('msg_max_signature_allowed');
    }
    if (errorElement) {
      this.handleError = true;
      this.form.selectSection(errorElement);
    }
  }

  private updateTitle(): void {
    const { name } = getFormStateSnapshotValue(this.masterInfo);
    const modeTitleTranslation = this.translations.get(this.modeTitle);
    this.title = {
      [FormMode.CreateMode]: modeTitleTranslation,
      [FormMode.EditMode]: `${modeTitleTranslation}: ${name}`,
      [FormMode.ViewMode]: `${modeTitleTranslation}: ${name}`,
    }[this.form.mode];
  }

  private isSimilar(obj1: any, obj2: any): boolean {
    let result = true;
    for (const key in obj1) {
      if (obj1.hasOwnProperty(key)) {
        if (!obj2.hasOwnProperty(key) || obj1[key] !== obj2[key]) {
          result = false;
        }
      }
    }
    for (const key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        if (!obj1.hasOwnProperty(key) || obj2[key] !== obj1[key]) {
          result = false;
        }
      }
    }
    return result;
  }

  private updateTree(): void {
    this.tree.treeModel.update();
  }

}
